import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, BookUser } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useT } from '../../services/i18n';
import CheckoutCarrierSection from './CheckoutCarrierSection';
import CheckoutBillingSection from './CheckoutBillingSection';

const FIELDS = [
  { name: 'firstName', key: 'firstName', ph: 'Jean', required: true },
  { name: 'lastName', key: 'lastName', ph: 'Dupont', required: true },
  { name: 'email', key: 'email', ph: 'jean.dupont@email.com', required: true, type: 'email' },
  { name: 'phone', key: 'phone', ph: '06 12 34 56 78', required: true },
  { name: 'address', key: 'address', ph: '123 rue de la République', required: true, wide: true },
  { name: 'addressComplement', key: 'addressComplement', phKey: 'checkout.complementPlaceholder', wide: true },
  { name: 'postalCode', key: 'postalCode', ph: '75001', required: true, maxLength: 5 },
  { name: 'city', key: 'city', ph: 'Paris', required: true },
];

const CheckoutShippingStep = ({ shippingInfo, onChange, addresses, selectedAddress, applyAddress, shipOptions, carrierProps, billingProps, formError, onContinue }) => {
  const { t } = useT();
  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <h2 className="text-xl font-bold text-gray-900 mb-6 flex items-center gap-2">
        <MapPin className="text-orange-500" size={24} />
        {t('checkout.shippingAddress')}
      </h2>

      {addresses.length > 0 && (
        <div className="mb-5" data-testid="checkout-address-book">
          <p className="text-xs font-medium text-gray-600 mb-2 flex items-center gap-1"><BookUser size={13} /> {t('checkout.savedAddresses')}</p>
          <div className="flex flex-wrap gap-2">
            {addresses.map(a => (
              <button type="button" key={a.id} onClick={() => applyAddress(a)} data-testid={`checkout-address-${a.id}`}
                className={`text-left text-xs border rounded-lg px-3 py-2 max-w-[240px] transition-colors ${selectedAddress === a.id ? 'border-orange-500 bg-orange-50 ring-1 ring-orange-300' : 'border-gray-200 hover:border-orange-300'}`}>
                <span className="font-semibold text-gray-900 block">{a.label || t('checkout.address')}{a.is_default_shipping ? t('checkout.default') : ''}</span>
                <span className="text-gray-600">{a.first_name} {a.last_name} — {a.address}, {a.postal_code} {a.city}</span>
              </button>
            ))}
            <Link to="/mon-compte/adresses" className="text-xs border border-dashed rounded-lg px-3 py-2 text-gray-500 hover:text-orange-600 hover:border-orange-300 flex items-center" data-testid="checkout-manage-addresses">{t('checkout.manageAddresses')}</Link>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {FIELDS.map((f) => (
          <div key={f.name} className={f.wide ? 'md:col-span-2' : undefined}>
            <label className="block text-sm font-medium text-gray-700 mb-1">{t(`checkout.${f.key}`)}{f.required ? ' *' : ''}</label>
            <Input name={f.name} data-testid={`checkout-${f.name}-input`} type={f.type} value={shippingInfo[f.name]} onChange={onChange} placeholder={f.phKey ? t(f.phKey) : f.ph} maxLength={f.maxLength} />
          </div>
        ))}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('checkout.country')} *</label>
          <select name="country" data-testid="checkout-country-select" value={shippingInfo.country} onChange={onChange} className="w-full h-10 border border-gray-300 rounded-md px-3 text-sm bg-white">
            {(shipOptions.countries.length ? shipOptions.countries : [{ code: 'FR', name: 'France' }]).map((c) => <option key={c.code} value={c.name}>{c.name}</option>)}
            {!shipOptions.countries.some((c) => c.name === shippingInfo.country) && shippingInfo.country && <option value={shippingInfo.country}>{shippingInfo.country}</option>}
          </select>
        </div>
      </div>

      <CheckoutCarrierSection shipOptions={shipOptions} shippingInfo={shippingInfo} {...carrierProps} />
      <CheckoutBillingSection addresses={addresses} {...billingProps} />

      <div className="mt-6 flex items-center justify-between gap-4">
        {formError ? <p data-testid="checkout-error" className="text-sm text-red-600">{formError}</p> : <span />}
        <Button data-testid="checkout-continue-button" onClick={onContinue} className="bg-orange-500 hover:bg-orange-600">
          {t('checkout.continue')}
        </Button>
      </div>
    </div>
  );
};

export default CheckoutShippingStep;
